import React, { useEffect } from 'react';
import './ShareCard.css';

function getCategoryInitial(category) {
  const map = {
    video: 'V',
    pinComment: 'P',
    email: 'E',
    community: 'C',
  };
  return map[category] || (category ? category.charAt(0).toUpperCase() : '?');
}

/* Card dimensions: 600 × 600 px (1:1 square) */
const CARD_W = 600;
const CARD_H = 600;
const INNER_W = CARD_W - 128;          /* 600 - 2×16 side padding = 568 */
const THUMB_H = Math.round(INNER_W * 9 / 16); /* true 16:9 → 320px */

const ShareCard = React.forwardRef(function ShareCard(
  { sale, currencySymbol, onReady },
  ref
) {
  const isVideo =
    sale.category === 'video' || sale.category === 'pinComment';
  const thumbnailUrl = isVideo
    ? `https://img.youtube.com/vi/${sale.name}/mqdefault.jpg`
    : null;
  const title = (sale.youtube_title || sale.name || 'Untitled').trim();

  /* Non-video rows have no image to wait for — fire onReady immediately. */
  useEffect(() => {
    if (!isVideo) {
      const t = setTimeout(onReady, 200);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmt = (n) => new Intl.NumberFormat().format(Number(n) || 0);
  const fmtRevenue = (n) =>
    `${currencySymbol}${Number(n || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const statsRows = [
    [
      { label: 'Views',   value: fmt(sale.views) },
      { label: 'Clicks',  value: fmt(sale.totalClicks) },
    ],
    [
      { label: 'Sales',   value: fmt(sale.totalSales) },
      { label: 'Revenue', value: fmtRevenue(sale.totalRevenue) },
    ],
  ];

  return (
    <div
      ref={ref}
      style={{
        width: `${CARD_W}px`,
        height: `${CARD_H}px`,
        background: 'linear-gradient(175deg, #0a0e1f 0%, #0F1224 40%, #1B1E2F 100%)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        overflow: 'hidden',
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      {/* ── Thumbnail ── */}
      <div
        style={{
          padding: '12px 64px 0 64px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
      >
      <div
        style={{
          width: '100%',
          height: `${THUMB_H}px`,
          position: 'relative',
          backgroundColor: '#1B1E2F',
          overflow: 'hidden',
          borderRadius: '10px',
        }}
      >
        {isVideo && (
          <img
            src={thumbnailUrl}
            alt="thumbnail"
            crossOrigin="anonymous"
            onLoad={onReady}
            onError={onReady}
            style={{
              position: 'absolute',
              top: 0, left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        )}
        {!isVideo && (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              background: 'linear-gradient(135deg, #1B1E2F 0%, #272A3A 100%)',
            }}
          >
            <div
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 211, 242, 0.12)',
                border: '2px solid rgba(0, 211, 242, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '30px',
                fontWeight: 700,
                color: '#00D3F2',
              }}
            >
              {getCategoryInitial(sale.category)}
            </div>
            <span
              style={{
                color: '#646B8B',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              {sale.category}
            </span>
          </div>
        )}


      </div>
      </div>

      {/* ── Title section ── */}
      <div
        style={{
          padding: '10px 24px 8px',
          flexShrink: 0,
        }}
      >
        {/* Category badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 211, 242, 0.1)',
            border: '1px solid rgba(0, 211, 242, 0.28)',
            borderRadius: '4px',
            padding: '3px 9px',
            marginBottom: '8px',
          }}
        >
          <span
            style={{
              color: '#00D3F2',
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.09em',
            }}
          >
            {sale.category}
          </span>
        </div>

        {/* Title */}
        <div
          style={{
            color: '#FFFFFF',
            fontSize: '20px',
            fontWeight: 700,
            lineHeight: 1.38,
            maxHeight: '56px',
            overflow: 'hidden',
            wordBreak: 'break-word',
            letterSpacing: '-0.015em',
          }}
        >
          {title}
        </div>
      </div>

      {/* ── Stats grid (2 × 2) ── */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          margin: '0 24px',
          gap: '0',
          borderRadius: '10px',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.07)',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
        }}
      >
        {statsRows.map((row, ri) => (
          <div
            key={ri}
            style={{
              display: 'flex',
              flexDirection: 'row',
              flex: 1,
              borderBottom:
                ri < statsRows.length - 1
                  ? '1px solid rgba(255, 255, 255, 0.07)'
                  : 'none',
            }}
          >
            {row.map((stat, ci) => (
              <div
                key={stat.label}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '12px 8px',
                  borderRight:
                    ci < row.length - 1
                      ? '1px solid rgba(255, 255, 255, 0.07)'
                      : 'none',
                }}
              >
                <span
                  style={{
                    color: '#646B8B',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}
                >
                  {stat.label}
                </span>
                <span
                  style={{
                    color: '#FFFFFF',
                    fontSize: '22px',
                    fontWeight: 700,
                    letterSpacing: '-0.03em',
                    lineHeight: 1,
                  }}
                >
                  {stat.value}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* ── Branding ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '7px',
          padding: '10px 24px 14px',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: '7px',
            height: '7px',
            borderRadius: '50%',
            backgroundColor: '#00D3F2',
            boxShadow: '0 0 6px rgba(0, 211, 242, 0.6)',
            flexShrink: 0,
          }}
        />
        <span
          style={{
            color: '#474D69',
            fontSize: '12px',
            fontWeight: 500,
            letterSpacing: '0.04em',
          }}
        >
          Powered by RevTrack
        </span>
      </div>
    </div>
  );
});

export default ShareCard;
